import type { EntriesResolution } from '../api.js';

/**
 * The window a node's chart is showing: how much time is on screen, and how
 * far back from the most recent reading it sits.
 *
 * Kept out of the component and pure, so the parts that are easy to get
 * subtly wrong — a log scale that has to feel even under the finger, a pan
 * that must stop at the ends of the history rather than drift into empty
 * decades — are arithmetic a test can pin down rather than behaviour somebody
 * has to drag to check.
 *
 * Replaces four buttons offering 30 days, 90 days, 6 months and all of it,
 * which is a coarse way to look at seven years of readings.
 */

const DAY_MS = 86_400_000;

/** The narrowest the chart will go. Below a week the line stops being a line. */
export const MIN_SPAN_DAYS = 7;

/**
 * Slider position runs 0–1000 rather than 0–100 so a nudge with an arrow key
 * moves a sensible amount at every zoom: on a log scale a single step near
 * the wide end is worth weeks, and 100 steps would make the last few
 * unusable.
 */
export const ZOOM_STEPS = 1000;

/**
 * Slider position to a span in days, on a log scale.
 *
 * Linear would be useless here. Half way along a linear scale from a week to
 * seven years is three and a half years, so every span a person actually
 * wants — a month, a season — would be crushed into the first few percent of
 * the track. On a log scale each step is the same proportional change, which
 * is what "closer together or further apart" means to the hand moving it.
 */
export function spanDaysForZoom(zoom: number, maxSpanDays: number): number {
  const max = Math.max(MIN_SPAN_DAYS, maxSpanDays);
  const t = Math.min(1, Math.max(0, zoom / ZOOM_STEPS));
  const span = Math.exp(Math.log(MIN_SPAN_DAYS) + t * (Math.log(max) - Math.log(MIN_SPAN_DAYS)));
  return Math.round(span);
}

/** The inverse, for setting the slider from a span the screen already has. */
export function zoomForSpanDays(spanDays: number, maxSpanDays: number): number {
  const max = Math.max(MIN_SPAN_DAYS, maxSpanDays);
  const span = Math.min(max, Math.max(MIN_SPAN_DAYS, spanDays));
  if (max === MIN_SPAN_DAYS) return ZOOM_STEPS;
  const t = (Math.log(span) - Math.log(MIN_SPAN_DAYS)) / (Math.log(max) - Math.log(MIN_SPAN_DAYS));
  return Math.round(t * ZOOM_STEPS);
}

/**
 * How much of a reading's history there is to look at, in days.
 *
 * A node with one reading, or none, still needs a scale to draw; the minimum
 * is what stops the slider having nowhere to go.
 */
export function historySpanDays(oldestIso: string | null, latestIso: string | null): number {
  if (oldestIso === null || latestIso === null) return MIN_SPAN_DAYS;
  const oldest = Date.parse(oldestIso);
  const latest = Date.parse(latestIso);
  if (Number.isNaN(oldest) || Number.isNaN(latest)) return MIN_SPAN_DAYS;
  return Math.max(MIN_SPAN_DAYS, Math.ceil((latest - oldest) / DAY_MS));
}

/**
 * Which grouping a span deserves.
 *
 * Derived from the zoom rather than chosen separately — the owner asked for
 * that, knowing it reverses an earlier decision of theirs. The thresholds are
 * about how many marks land on a plot a few hundred pixels wide: a year of
 * individual weigh-ins is a smear, and a fortnight of weekly averages is four
 * points and no shape at all.
 */
export function resolutionForSpanDays(spanDays: number): EntriesResolution {
  if (spanDays <= 45) return 'raw';
  if (spanDays <= 400) return 'daily';
  return 'weekly';
}

/**
 * The grouping override: 'auto' hands the decision back to the zoom.
 */
export type Grouping = 'auto' | EntriesResolution;

/**
 * What ticking or unticking "Average per week" means.
 *
 * Ticking it at a zoom the automatic rule already calls weekly goes back to
 * 'auto' rather than pinning weekly: the box is already in the state being
 * asked for, and pinning would leave the reader with an override they never
 * set and cannot see.
 *
 * Unticking at such a zoom falls to DAILY, not to raw. Raw is what the zoom
 * would pick for a fortnight, and a decade of individual weigh-ins is several
 * thousand points drawn across a few hundred pixels — a band of ink rather
 * than a chart, and a far larger response than the screen can use. Daily is
 * the finest grouping that still says something at that width.
 */
export function groupingAfterWeeklyToggle(checked: boolean, auto: EntriesResolution): Grouping {
  if (checked) return auto === 'weekly' ? 'auto' : 'weekly';
  return auto === 'weekly' ? 'daily' : 'auto';
}

export interface ChartWindow {
  from: string;
  to: string;
}

/**
 * The window itself, as ISO strings.
 *
 * `offsetDays` is how far back the window's right edge sits from the most
 * recent reading. Zero means the window ends at the latest reading, which is
 * where a node opens and where it returns when you zoom all the way out.
 */
export function windowFor(
  latestIso: string,
  spanDays: number,
  offsetDays: number,
): ChartWindow {
  const latest = Date.parse(latestIso);
  const to = latest - offsetDays * DAY_MS;
  return {
    from: new Date(to - spanDays * DAY_MS).toISOString(),
    to: new Date(to).toISOString(),
  };
}

/**
 * Keeps a pan inside the history.
 *
 * Panning forward stops at the latest reading — there is nothing after it but
 * an empty future, and a chart that scrolls into it looks broken rather than
 * finished. Panning back stops once the window's left edge reaches the oldest
 * reading, so the furthest you can go is the first thing you ever recorded
 * sitting at the left of the plot.
 *
 * When the whole history is narrower than the window, the answer is always
 * zero: everything is already on screen and there is nowhere to go.
 */
export function clampOffsetDays(
  offsetDays: number,
  spanDays: number,
  oldestIso: string | null,
  latestIso: string | null,
): number {
  if (oldestIso === null || latestIso === null) return 0;
  const oldest = Date.parse(oldestIso);
  const latest = Date.parse(latestIso);
  if (Number.isNaN(oldest) || Number.isNaN(latest)) return 0;

  const historyDays = (latest - oldest) / DAY_MS;
  const maxOffset = historyDays - spanDays;
  if (maxOffset <= 0) return 0;
  return Math.min(maxOffset, Math.max(0, offsetDays));
}

/** The points inside a window, for handing to a chart that draws only those. */
export function pointsInWindow<T extends { recordedAt: string }>(
  points: readonly T[],
  window: ChartWindow,
): T[] {
  const from = Date.parse(window.from);
  const to = Date.parse(window.to);
  if (Number.isNaN(from) || Number.isNaN(to)) return [...points];
  return points.filter((point) => {
    const at = Date.parse(point.recordedAt);
    return !Number.isNaN(at) && at >= from && at <= to;
  });
}

/**
 * The points inside a window, plus the nearest one on each side of it.
 *
 * Without the neighbours a line begins at the first reading inside the frame
 * and ends at the last, so the segments that cross the edges are simply
 * missing: scroll back through the history and the line keeps starting a
 * little way in, as though the measurements began there. The two extra points
 * are never seen — the chart clips to its plot — but the segments they anchor
 * are, which is the whole point of including them.
 *
 * Returned in time order, so the caller can treat the result as an ordinary
 * series.
 */
export function pointsInWindowWithBleed<T extends { recordedAt: string }>(
  points: readonly T[],
  window: ChartWindow,
): T[] {
  const from = Date.parse(window.from);
  const to = Date.parse(window.to);
  if (Number.isNaN(from) || Number.isNaN(to)) return [...points];

  const inside: T[] = [];
  let before: T | null = null;
  let after: T | null = null;

  for (const point of points) {
    const at = Date.parse(point.recordedAt);
    if (Number.isNaN(at)) continue;
    if (at < from) {
      // The list is in time order, so each earlier point replaces the last.
      before = point;
    } else if (at > to) {
      // The first one past the window is the only one worth keeping.
      if (after === null) after = point;
    } else {
      inside.push(point);
    }
  }

  return [...(before ? [before] : []), ...inside, ...(after ? [after] : [])];
}

/**
 * How a span reads out loud, for the slider's own label and its
 * `aria-valuetext`.
 *
 * The number a range input carries is an index into a scale; "4 months" is
 * the thing actually being chosen, and "612" tells a reader nothing.
 */
export function describeSpanDays(spanDays: number): string {
  if (spanDays < 14) return `${spanDays} days`;
  if (spanDays < 60) {
    const weeks = Math.round(spanDays / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'}`;
  }
  if (spanDays < 730) {
    const months = Math.round(spanDays / 30.44);
    return `${months} month${months === 1 ? '' : 's'}`;
  }
  const years = Math.round((spanDays / 365.25) * 10) / 10;
  return `${years} year${years === 1 ? '' : 's'}`;
}

/**
 * Rounds a range outward onto a grid of round numbers.
 *
 * This is what actually stops a y-axis shuddering while somebody scrolls.
 * Holding the previous axis until the readings no longer fit is not enough on
 * its own: an axis drawn tight to noisy readings is exceeded, then shrinks,
 * then is exceeded again, and the first attempt at this still produced 48
 * different axes over 61 steps of scrolling. Snapped to a grid, the ordinary
 * wander of the next few windows lands inside the same box and the axis does
 * not move at all — and when it finally does, it moves to another round
 * number rather than to whatever the noise happened to reach.
 *
 * The step is 1, 2 or 5 times a power of ten, the same family of intervals
 * the ticks themselves use, so the snapped bounds tend to fall on ticks.
 */
export function snapRange(min: number, max: number): { min: number; max: number } {
  const span = max - min;
  if (!Number.isFinite(span) || span <= 0) return { min, max };

  // Aim for roughly four steps across the range, then take the nearest round
  // one at or above that.
  const rough = span / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalised = rough / magnitude;
  const step = (normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10) * magnitude;

  return {
    min: Math.floor(min / step) * step,
    max: Math.ceil(max / step) * step,
  };
}

/** Slider positions for the value axis, on the same 0–1000 scale as the zoom. */
export const RANGE_STEPS = 1000;

/**
 * The narrowest range worth offering, for a node of this magnitude.
 *
 * Ten steps at the node's own precision is the floor — a kilo for a weight
 * read to one decimal, ten mmHg for a blood pressure read to none — but it
 * cannot be the whole answer, because these charts are not only for weight. A
 * custom number counting steps runs to twenty thousand, and a slider whose
 * narrow end is one step would spend nine tenths of its track on ranges
 * nobody would ever read. So it is also never finer than a hundredth of the
 * history, whatever that history happens to measure.
 */
export function narrowestRange(fullSpan: number, precision: number): number {
  return Math.max(10 * 10 ** -precision, fullSpan / 100);
}

/**
 * How many units of the measurement the y-axis shows, from a slider position.
 *
 * Logarithmic for the same reason the time zoom is: half way along a linear
 * scale from one kilo to forty is twenty, so every range anybody actually
 * wants to read a weight at would sit in the first few percent of the track.
 *
 * The wide end is the whole history, so the furthest right always shows
 * everything and nothing can hide off-screen — and it grows on its own as the
 * history does. Fifteen years and thirty kilos later, "Everything" still
 * means everything.
 */
export function rangeSpanForZoom(zoom: number, fullSpan: number, precision: number): number {
  const narrow = narrowestRange(fullSpan, precision);
  const wide = Math.max(narrow, fullSpan);
  const t = Math.min(1, Math.max(0, zoom / RANGE_STEPS));
  return Math.exp(Math.log(narrow) + t * (Math.log(wide) - Math.log(narrow)));
}

/** The inverse, for placing the slider from a span already in use. */
export function zoomForRangeSpan(span: number, fullSpan: number, precision: number): number {
  const narrow = narrowestRange(fullSpan, precision);
  const wide = Math.max(narrow, fullSpan);
  if (wide === narrow) return RANGE_STEPS;
  const clamped = Math.min(wide, Math.max(narrow, span));
  return Math.round(((Math.log(clamped) - Math.log(narrow)) / (Math.log(wide) - Math.log(narrow))) * RANGE_STEPS);
}

/**
 * Where to centre an axis of a given height.
 *
 * Holds the centre still until the readings have genuinely walked away from
 * it, and then moves in one go.
 *
 * The test is whether the MIDDLE of the readings is still on the axis, not
 * whether all of them fit. Requiring them all to fit was the first attempt and
 * it made things worse rather than better: choose a range narrower than the
 * readings — which is the whole point of choosing a narrow one — and they can
 * never all fit, so it re-centred on every single pan step. Sixty-one steps of
 * scrolling produced fifty-eight different axes.
 *
 * Never moving at all is the other wrong answer: scroll back to a year twenty
 * kilos heavier and the line simply leaves the screen.
 */
export function centreForRange(
  previousCentre: number | null,
  span: number,
  dataMin: number,
  dataMax: number,
): number {
  const dataCentre = (dataMin + dataMax) / 2;
  if (previousCentre === null) return dataCentre;
  const half = span / 2;
  // Inside the frame, with a little to spare so it does not flicker on the
  // boundary: still this axis's business, leave it alone.
  if (Math.abs(dataCentre - previousCentre) <= half * 0.75) return previousCentre;
  return dataCentre;
}

/**
 * A height for the axis that does not change every time the reader nudges the
 * chart sideways.
 *
 * The centre being held is only half of holding still: a height recomputed
 * from the readings on screen moves the axis just as surely, and leaving it
 * that way made the automatic mode the worst of the three — sixty different
 * axes over sixty-one steps of scrolling, which is what somebody who never
 * touches the range slider would have got.
 *
 * So it is rounded to one, two or five times a power of ten, and then kept:
 * it grows when the readings no longer fit, and shrinks only once they have
 * dropped below a third of it, so the ordinary wander of the next few windows
 * changes nothing at all.
 */
export function heldSpan(previous: number | null, needed: number, floor: number): number {
  const wanted = roundUpToNiceNumber(Math.max(needed, floor));
  if (previous === null) return wanted;
  if (needed > previous) return Math.max(previous, wanted);
  if (needed >= previous / 3) return previous;
  return wanted;
}

/** 1, 2 or 5 times a power of ten, at or above the value given. */
function roundUpToNiceNumber(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return value;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalised = value / magnitude;
  return (normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10) * magnitude;
}
